import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useUpdateAgentMemoryEntry } from '@/hooks'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import type { AgentMemoryEntry } from '@/types'
import { Check } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'

const labelClass = 'text-xs font-bold leading-[160%] tracking-[0.015em] text-foreground'

const CONTENT_MAX = 4000

interface EditMemoryDialogFormValues {
  content: string
}

interface EditMemoryDialogProps {
  projectId: string
  entry: Pick<AgentMemoryEntry, 'id' | 'agentId' | 'content'>
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function EditMemoryDialog({ projectId, entry, open, onOpenChange }: EditMemoryDialogProps) {
  const updateMemory = useUpdateAgentMemoryEntry(projectId, entry.agentId)
  const { register, handleSubmit, reset, formState: { errors } } = useForm<EditMemoryDialogFormValues>({
    values: { content: entry.content },
    resetOptions: { keepDirtyValues: true },
  })

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) reset()
    onOpenChange(isOpen)
  }

  const onSubmit = async (data: EditMemoryDialogFormValues) => {
    try {
      await updateMemory.mutateAsync({
        entryId: entry.id,
        data: { content: data.content.trim() },
      })
      toast.success('Memory entry updated')
      handleClose(false)
    } catch (error) {
      toast.error(extractValidationMessage(error) ?? getErrorMessage(error) ?? 'Failed to update memory entry')
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[640px] rounded-2xl p-8 gap-6">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">Edit Memory</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-memory-content" className={labelClass}>Memory Description</Label>
            <Textarea
              id="edit-memory-content"
              {...register('content', {
                validate: v => v.trim().length > 0 || 'Memory content is required',
                maxLength: { value: CONTENT_MAX, message: `Content must not exceed ${CONTENT_MAX} characters` },
              })}
              rows={3}
            />
            {errors.content && <p className="text-sm text-destructive" role="alert">{errors.content.message}</p>}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => handleClose(false)}>Cancel</Button>
            <Button type="submit" disabled={updateMemory.isPending}>
              <Check className="h-4 w-4" />
              {updateMemory.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
